import { Quaternion, Vector3 } from 'three'

import { AnyID, SnapSide } from '@types'

import { getMorphedItemWidth } from './projectItemOnSlabFaces'
import { getRinconeroLinkDistance, isRinconeroNode } from './rinconeroGeometry'
import { getObbCenter, getSideFacing, isSlabId, setSnappedSide } from './snappedSides'
import { SNAP_SIDES, type SnapGroupNode } from './snapGroups'

/**
 * Deja el registro de caras coherente con la geometría de la escena.
 *
 * `rebuildSnappedSides` solo RELLENA caras libres, y solo se ejecuta al cargar el proyecto:
 * lo que escribe el arrastre no se revisa nunca más. Eso deja dos agujeros que rompen a los
 * consumidores del registro (laterales, frontales, encimeras):
 *
 * 1. Etiquetas que contradicen la geometría. collideItems decide la cara del giro en L de un
 *    rinconero por convenio (`lSide`: izquierda si el rinconero es derecho), no midiendo, y
 *    `assignRunSlots` se cree los left/right al pie de la letra. Cuando el convenio y la
 *    posición real no coinciden, el rinconero acaba con el extremo equivocado marcado como
 *    ocupado: el que de verdad tiene al vecino queda libre y se le dibuja un lateral encima
 *    del mueble siguiente.
 * 2. Enganches que nunca llegan a registrarse. El mueble que continúa la fila por el giro de
 *    un rinconero no toca cara con cara: queda desplazado a lo ancho Y hacia delante (el
 *    fondo más el saliente de la regleta), así que el test de contacto plano de
 *    `rebuildSnappedSides` no lo ve y la fila se parte en la esquina.
 *
 * Aquí no se inventa nada: cada cara se decide midiendo, con las mismas cuentas que usa el
 * dibujo. Es idempotente y no borra enlaces, solo los coloca en la cara que les toca y añade
 * los que faltan.
 */

type ItemsLike = { has: (id: AnyID) => boolean; get?: (id: AnyID) => unknown }

export type ReconcileResult = {
  /** Enlaces que estaban en una cara que no se corresponde con la posición real */
  relabeled: Array<{ id: AnyID; other: AnyID; from: SnapSide; to: SnapSide }>
  /** Giros de rinconero que no estaban registrados */
  linked: Array<{ rinconero: AnyID; other: AnyID; side: SnapSide; otherSide: SnapSide }>
}

type NodeInfo = {
  node: SnapGroupNode
  center: Vector3
  quaternion: Quaternion
}

const getNodeInfo = (node: SnapGroupNode): NodeInfo => {
  const position = new Vector3()
  const quaternion = new Quaternion()
  node.matrix.decompose(position, quaternion, new Vector3())
  return {
    node,
    quaternion,
    center: getObbCenter(
      position,
      quaternion,
      getMorphedItemWidth(node),
      node.height || 0,
      node.thickness || 0,
    ),
  }
}

/**
 * @param items todos los objetos de la escena, para distinguir referencias vivas
 * @param candidates items que participan en el registro de caras (el mismo filtro que use
 *        el llamador para agrupar: `getSnapCandidates`)
 */
export function reconcileSnappedSides(
  items: ItemsLike,
  candidates: Iterable<SnapGroupNode>,
): ReconcileResult {
  const nodes = [...candidates].filter((node) => node?.id && node.matrix)
  const infoById = new Map<AnyID, NodeInfo>()
  for (const node of nodes) infoById.set(node.id as AnyID, getNodeInfo(node))

  const result: ReconcileResult = { relabeled: [], linked: [] }

  // 1) Cada enlace ya registrado, a la cara que le corresponde por posición.
  //    Las losas se dejan como están: su cara la decide el tramo de la pared, no el centro.
  for (const node of nodes) {
    const sides = node.snappedSides
    if (!sides) continue
    const self = infoById.get(node.id as AnyID)!

    for (const side of SNAP_SIDES) {
      const otherId = sides[side]
      if (!otherId || isSlabId(otherId) || !items.has(otherId)) continue
      const other = infoById.get(otherId)
      if (!other) continue

      const desired = getSideFacing(self.quaternion, self.center, other.center)
      if (desired === side) continue

      const occupant = sides[desired]
      // La cara buena ya la ocupa otro mueble: mover el enlace ahí lo perdería, así que
      // se deja donde está (peor etiquetado, pero sin romper el grafo).
      if (occupant && occupant !== otherId) continue

      delete sides[side]
      sides[desired] = otherId
      result.relabeled.push({ id: node.id as AnyID, other: otherId, from: side, to: desired })
    }
  }

  // 2) Giros de rinconero que el test de contacto plano no puede ver.
  for (const node of nodes) {
    if (!isRinconeroNode(node)) continue
    const self = infoById.get(node.id as AnyID)!

    let best: { other: SnapGroupNode; distance: number } | null = null
    for (const candidate of nodes) {
      if (candidate.id === node.id) continue
      const distance = getRinconeroLinkDistance(node, candidate)
      if (distance === null) continue
      if (!best || distance < best.distance) best = { other: candidate, distance }
    }
    if (!best) continue

    const otherId = best.other.id as AnyID
    // Ya se conocen (por el arrastre o por el paso anterior): no duplicar el enlace.
    if (SNAP_SIDES.some((side) => node.snappedSides?.[side] === otherId)) continue
    if (SNAP_SIDES.some((side) => best.other.snappedSides?.[side] === node.id)) continue

    const other = infoById.get(otherId)!
    const side = getSideFacing(self.quaternion, self.center, other.center)
    const otherSide = getSideFacing(other.quaternion, other.center, self.center)
    // Si alguna de las dos caras está ocupada por un tercero, el hueco no es este enganche.
    if (node.snappedSides?.[side] || best.other.snappedSides?.[otherSide]) continue

    setSnappedSide(node, side, otherId)
    setSnappedSide(best.other, otherSide, node.id as AnyID)
    result.linked.push({ rinconero: node.id as AnyID, other: otherId, side, otherSide })
  }

  return result
}
